// Tuner Service with iOS / Safari optimizations and smoothing
/* eslint-disable no-empty */
import { reactive } from 'vue';
import { AudioEngine } from './audioEngine';

class TunerService {
  constructor() {
    this.audioStream = null;
    this.sourceNode = null;
    this.preampGain = null;
    this.analyser = null;
    this.silentGain = null;
    this.animFrameId = null;

    this.pitchHistory = [];
    this.smoothedCentsVal = 0;
    this.silenceFrames = 0;
    this.inTuneFrames = 0;
    this.sustainVal = 0;

    this.state = reactive({
      isListening: false,
      note: null,          // { name, octave, frequency, cents, midi }
      liveFrequency: null,
      smoothedCents: 0,
      signalLevel: 0,      // 0 to 100
      clarity: 0,          // 0 to 100 — confiança bruta da detecção YIN
      sustain: 0,          // 0 to 100 — arco interno (sustentação/confiança suavizada)
      inTune: false,
      error: null,
      errorKey: null,      // chave i18n tuner.* para exibir mensagem amigável
      a4Calibration: 440,
      noiseThreshold: 0.008,
    });
  }

  isIOSorSafari() {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isIOS || isSafari;
  }

  async start() {
    if (this.state.isListening) return true;
    this.state.error = null;
    this.state.errorKey = null;
    const requestMic = async (constraints) =>
      navigator.mediaDevices.getUserMedia(constraints);

    const mapError = (err) => {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'micPermissionDenied';
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'micNoDevice';
      if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') return 'micBusy';
      if (name === 'SecurityError') return 'micInsecureContext';
      if (name === 'OverconstrainedError' || name === 'TypeError') return 'micConstraints';
      return 'micGeneric';
    };

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.state.errorKey = 'micUnsupported';
        this.state.error = 'Microphone access requires HTTPS or a secure context.';
        return false;
      }
      if (typeof window !== 'undefined' && 'isSecureContext' in window && !window.isSecureContext) {
        this.state.errorKey = 'micInsecureContext';
        this.state.error = 'Microphone access requires HTTPS or a secure context.';
        return false;
      }

      const ctx = AudioEngine.getAudioContext();
      if (ctx.state === 'suspended' || ctx.state === 'interrupted') {
        await ctx.resume();
      }

      let stream;
      try {
        // Specific audio constraints for Safari / iOS to prevent filtering instruments
        stream = await requestMic({
          audio: {
            echoCancellation: false,
            autoGainControl: true,
            noiseSuppression: false,
          },
        });
      } catch (firstErr) {
        // Só tenta o fallback genérico quando o problema são as constraints.
        // Nos demais casos (permissão negada, sem dispositivo, etc.) um 2º
        // getUserMedia só dispara outro prompt e mascara o erro real.
        const retryable = firstErr?.name === 'OverconstrainedError' || firstErr?.name === 'TypeError';
        if (!retryable) throw firstErr;
        stream = await requestMic({ audio: true });
      }

      stream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });

      this.audioStream = stream;
      const source = ctx.createMediaStreamSource(stream);
      this.sourceNode = source;

      // Software Pre-Amp gain to equalize input level on iPad / iOS microphones
      // iOS applies aggressive mic attenuation; 3.5x pre-amp ensures low guitar/bass notes register
      const preamp = ctx.createGain();
      const gainMultiplier = this.isIOSorSafari() ? 3.5 : 1.5;
      preamp.gain.setValueAtTime(gainMultiplier, ctx.currentTime);
      source.connect(preamp);
      this.preampGain = preamp;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.1;
      preamp.connect(analyser);

      // WebKit / iOS Safari PWA compatibility:
      // iOS Safari requires a path connected to ctx.destination in order to pull audio buffers into AnalyserNode.
      const silentGain = ctx.createGain();
      silentGain.gain.setValueAtTime(0, ctx.currentTime);
      analyser.connect(silentGain);
      silentGain.connect(ctx.destination);
      this.silentGain = silentGain;

      this.analyser = analyser;

      if (ctx.state !== 'running') {
        await ctx.resume();
      }

      this.state.isListening = true;
      this.startPitchLoop();
      return true;
    } catch (err) {
      console.error('Error starting tuner:', err);
      this.state.errorKey = mapError(err);
      this.state.error = err.message || 'Microphone permission error';
      this.stop({ keepError: true });
      return false;
    }
  }

  stop({ keepError = false } = {}) {
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((t) => t.stop());
      this.audioStream = null;
    }
    if (this.silentGain) {
      try { this.silentGain.disconnect(); } catch (_) {}
      this.silentGain = null;
    }
    if (this.preampGain) {
      try { this.preampGain.disconnect(); } catch (_) {}
      this.preampGain = null;
    }
    if (this.sourceNode) {
      try { this.sourceNode.disconnect(); } catch (_) {}
      this.sourceNode = null;
    }
    if (this.analyser) {
      try { this.analyser.disconnect(); } catch (_) {}
      this.analyser = null;
    }
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }

    this.pitchHistory = [];
    this.smoothedCentsVal = 0;
    this.silenceFrames = 0;
    this.inTuneFrames = 0;
    this.sustainVal = 0;

    this.state.isListening = false;
    this.state.note = null;
    this.state.liveFrequency = null;
    this.state.smoothedCents = 0;
    this.state.signalLevel = 0;
    this.state.clarity = 0;
    this.state.sustain = 0;
    this.state.inTune = false;
    if (!keepError) {
      this.state.error = null;
      this.state.errorKey = null;
    }
  }

  startPitchLoop() {
    const analyser = this.analyser;
    if (!analyser) return;

    const bufferLength = analyser.fftSize;
    const timeDomainBuffer = new Float32Array(bufferLength);
    const byteBuffer = new Uint8Array(bufferLength);

    const updatePitch = () => {
      if (!this.state.isListening) return;

      if (analyser.getFloatTimeDomainData) {
        analyser.getFloatTimeDomainData(timeDomainBuffer);
      } else {
        analyser.getByteTimeDomainData(byteBuffer);
        for (let i = 0; i < bufferLength; i++) {
          timeDomainBuffer[i] = (byteBuffer[i] - 128) / 128;
        }
      }

      // Calculate RMS (Signal Level)
      let sumSq = 0;
      for (let i = 0; i < bufferLength; i++) {
        sumSq += timeDomainBuffer[i] * timeDomainBuffer[i];
      }
      const rms = Math.sqrt(sumSq / bufferLength);

      // Logarithmic (dB) scaling: maps -52 dB .. 0 dB to 0% .. 100%
      const db = 20 * Math.log10(rms + 1e-5);
      const levelPercent = Math.max(0, Math.min(100, Math.round(((db + 52) / 52) * 100)));
      this.state.signalLevel = levelPercent;

      // Check if signal exceeds noise gate
      if (rms >= this.state.noiseThreshold) {
        this.silenceFrames = 0;
        const sampleRate = analyser.context?.sampleRate || AudioEngine.getAudioContext().sampleRate;
        const result = AudioEngine.detectPitchYIN(timeDomainBuffer, sampleRate, 0.20);

        if (result.frequency > 0 && result.clarity > 0.40) {
          const rawClarity = Math.max(0, Math.min(100, Math.round(result.clarity * 100)));
          this.state.clarity = rawClarity;
          // Arco interno: combina nível de sinal + clareza, com ataque rápido e decay lento
          // para não oscilar bruscamente quando a corda começa a decair.
          const target = Math.round(
            Math.max(this.state.signalLevel, rawClarity * 0.9)
          );
          const alpha = target > this.sustainVal ? 0.5 : 0.12;
          this.sustainVal = this.sustainVal * (1 - alpha) + target * alpha;
          this.state.sustain = Math.round(this.sustainVal);
          // Push to rolling pitch history buffer (last 3 samples)
          this.pitchHistory.push(result.frequency);
          if (this.pitchHistory.length > 3) {
            this.pitchHistory.shift();
          }

          // Compute median frequency from history
          const sortedFreqs = [...this.pitchHistory].sort((a, b) => a - b);
          const medianFreq = sortedFreqs[Math.floor(sortedFreqs.length / 2)];
          this.state.liveFrequency = medianFreq;

          const noteInfo = AudioEngine.frequencyToNote(medianFreq, this.state.a4Calibration);
          if (noteInfo) {
            this.state.note = noteInfo;

            // Smooth cents with adaptive low-pass filter
            const alpha = Math.abs(noteInfo.cents - this.smoothedCentsVal) > 20 ? 0.6 : 0.35;
            this.smoothedCentsVal = this.smoothedCentsVal * (1 - alpha) + noteInfo.cents * alpha;
            const roundedSmoothed = Math.round(this.smoothedCentsVal);
            this.state.smoothedCents = roundedSmoothed;

            // In-tune detection (within +- 3 cents)
            if (Math.abs(roundedSmoothed) <= 3 && result.clarity > 0.65) {
              this.inTuneFrames += 1;
              if (this.inTuneFrames >= 4) {
                this.state.inTune = true;
              }
            } else {
              this.inTuneFrames = 0;
              this.state.inTune = false;
            }
          }
        } else {
          // Pitch fraco/instável: decai o arco interno devagar em vez de zerar de golpe
          this.state.clarity = Math.round((result.clarity || 0) * 100);
          this.sustainVal = this.sustainVal * 0.88;
          this.state.sustain = Math.round(this.sustainVal);
          this.inTuneFrames = 0;
          this.state.inTune = false;
        }
      } else {
        this.silenceFrames += 1;
        // Decay suave do arco interno durante o silêncio
        this.sustainVal = this.sustainVal * 0.88;
        this.state.sustain = Math.round(this.sustainVal);
        this.state.clarity = 0;
        // When silence is detected (~300ms / 18 frames), clear note and reset gauge cleanly
        if (this.silenceFrames >= 18) {
          this.pitchHistory = [];
          this.state.note = null;
          this.state.liveFrequency = null;
          this.state.smoothedCents = 0;
          this.state.inTune = false;
        }
      }

      this.animFrameId = requestAnimationFrame(updatePitch);
    };

    this.animFrameId = requestAnimationFrame(updatePitch);
  }
}

export const tunerService = new TunerService();
